import { create } from 'zustand';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from './auth.store';
import { queryClient } from '@/lib/queryClient';

interface SocketState {
  socket: Socket | null;
  isConnected: boolean;

  // Actions
  connect: () => void;
  disconnect: () => void;
  subscribe: (userId: string) => void;
  subscribeToProject: (projectId: string) => void;
  unsubscribeFromProject: (projectId: string) => void;
}

export const useSocketStore = create<SocketState>()((set, get) => ({
  socket: null,
  isConnected: false,
  
  connect: () => {
    const { socket } = get();
    
    // Don't reconnect if already connected
    if (socket?.connected) return;
    
    const newSocket = io('/activity', {
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      // The server rejects sockets without a valid access token. A function so
      // every (re)connect sends the current token, not the one from first load.
      auth: (cb) => cb({ token: useAuthStore.getState().accessToken }),
    });
    
    newSocket.on('connect', () => {
      console.log('[WebSocket] Connected to activity');
      set({ isConnected: true });
      
      // Subscribe with user ID
      const user = useAuthStore.getState().user;
      if (user) {
        newSocket.emit('subscribe', { userId: user.id });
      }
    });
    
    newSocket.on('disconnect', () => {
      console.log('[WebSocket] Disconnected');
      set({ isConnected: false });
    });
    
    newSocket.on('activity', (activity) => {
      console.log('[WebSocket] New activity:', activity);
      // Invalidate activity queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['activity'] });
      queryClient.invalidateQueries({ queryKey: ['activity-unread-count'] });
      // Dispatch custom event for components to listen to
      window.dispatchEvent(new CustomEvent('new-activity', { detail: activity }));
    });
    
    newSocket.on('message:new', (data) => {
      console.log('[WebSocket] New message:', data);
      window.dispatchEvent(new CustomEvent('new-message', { detail: data }));
    });
    
    newSocket.on('message:reply', (data) => {
      console.log('[WebSocket] New reply:', data);
      window.dispatchEvent(new CustomEvent('new-reply', { detail: data }));
    });
    
    newSocket.on('task:updated', (data) => {
      console.log('[WebSocket] Task updated:', data);
      window.dispatchEvent(new CustomEvent('task-updated', { detail: data }));
    });

    newSocket.on('discussion:read', (data) => {
      window.dispatchEvent(new CustomEvent('discussion-read', { detail: data }));
    });

    newSocket.on('discussion:summary-updated', (data) => {
      console.log('[WebSocket] Discussion summary updated:', data);
      // Invalidate discussions query to refresh with new summary
      queryClient.invalidateQueries({ queryKey: ['discussions', data.projectId] });
      window.dispatchEvent(new CustomEvent('discussion-summary-updated', { detail: data }));
    });

    set({ socket: newSocket });
  },

  disconnect: () => {
    const { socket } = get();
    if (socket) {
      socket.disconnect();
      set({ socket: null, isConnected: false });
    }
  },

  subscribe: (userId: string) => {
    const { socket } = get();
    if (socket?.connected) {
      socket.emit('subscribe', { userId });
    }
  },

  subscribeToProject: (projectId: string) => {
    const { socket } = get();
    if (socket?.connected) {
      socket.emit('subscribe:project', { projectId });
    }
  },

  unsubscribeFromProject: (projectId: string) => {
    const { socket } = get();
    if (socket?.connected) {
      socket.emit('unsubscribe:project', { projectId });
    }
  },
}));


